import { ComponentWithTooltip } from 'components/ComponentWithTooltip';
import { ControlledCheckbox, ControlledTextField, ControlNumberField } from 'components/Form';
import type {
    WorkOrderChangeOtherCostItem,
    WorkOrderChangeSeasonData,
    WorkOrderOtherCostItem,
    WorkOrderSeasonData,
} from 'gql/graphql';
import { OtherCostCalculationTypeEnum } from 'gql/graphql';
import { replacerCost } from 'utils/replacerCost';

import { noun } from 'plural-ru';
import React, { useMemo } from 'react';
import type { Control } from 'react-hook-form';
import { useWatch } from 'react-hook-form';

import Styles from '../AddWorkOrderOther.module.css';

import { getIsChildFieldDisabled } from './helpers';

export const SeasonRenderer = ({
    data,
    control,
    season,
}: {
    data: (WorkOrderOtherCostItem | WorkOrderChangeOtherCostItem) & {
        id: number;
        calculatingType: OtherCostCalculationTypeEnum;
    };
    season: WorkOrderSeasonData | WorkOrderChangeSeasonData;
    control: Control;
}) => {
    const currentLimit =
        'workOrderChangeObjects' in data
            ? data?.seasonLimits?.find((limit) => limit?.workOrderChangeSeasonId === season.id)
            : data?.seasonLimits?.find((limit) => limit?.workOrderSeasonId === season.id);
    const controlInputName = `otherPriceValues.${String(data?.id)}.season_${String(season?.id)}.includedCost`;
    const controlCheckboxName = `otherPriceValues.${String(data?.id)}.season_${String(season?.id)}.isIncluded`;
    const controlValue = useWatch({
        control,
        name: controlInputName,
    });
    const checkboxValue = useWatch({
        control,
        name: `checkedIds.${String(data?.id)}`,
    });

    const currentSeasonRemainderCost = currentLimit?.remainderCost;

    if (data?.calculatingType === OtherCostCalculationTypeEnum.Calculation) {
        const isDeviated = replacerCost(controlValue) > currentSeasonRemainderCost;

        return (
            <ControlNumberField
                name={controlInputName}
                control={control}
                type="number"
                decimalScale={2}
                size="xs"
                disabled={!checkboxValue?.parent}
                leftSide={isDeviated ? '▲' : undefined}
                clearOnDisabled
                placeholder=""
                className={Styles.editableField}
            />
        );
    }
    if (data?.calculatingType === OtherCostCalculationTypeEnum.Percent) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                }}
            >
                <ControlledCheckbox
                    control={control}
                    name={controlCheckboxName}
                    disabled={!checkboxValue?.parent}
                    clearOnDisabled
                />
            </div>
        );
    }

    return null;
};

export const NoteColumnRenderer = ({
    data,
    control,
}: {
    control: Control;
    data: (WorkOrderOtherCostItem | WorkOrderChangeOtherCostItem) & {
        parentId: number;
        id: number;
        objectId: number;
        canRemoveOther: boolean;
        canAddOther: boolean;
        isIncluded: boolean;
    };
}) => {
    const controlInputName = `otherPriceNotes.${String(data?.parentId)}.${String(data?.objectId)}`;

    const checkboxValue = useWatch({
        control,
        name: `checkedIds.${String(data?.parentId)}`,
    });

    const isChildAndDisabled = useMemo(
        () =>
            getIsChildFieldDisabled({
                checkedIds: checkboxValue,
                data,
            }),
        [checkboxValue, data],
    );

    const isDisabled = useMemo(
        () => isChildAndDisabled || !checkboxValue?.[data?.objectId],
        [checkboxValue, data?.objectId, isChildAndDisabled],
    );

    if (data?.parentId) {
        return <ControlledTextField control={control} name={controlInputName} disabled={isDisabled} />;
    }

    return null;
};

export const CheckBoxRenderer = ({
    data,
    control,
    isWorkOrderChange,
}: {
    data: (WorkOrderOtherCostItem | WorkOrderChangeOtherCostItem) & {
        parentId: number;
        id: number;
        nestLevel?: number;
        canAddOther: boolean;
        canRemoveOther: boolean;
        isActKC2Cost: boolean;
        hasActualCost: boolean;
        isIncluded: boolean;
        objectId: number;
    };
    control: Control;
    isWorkOrderChange?: boolean;
}) => {
    const { checkedIds } = useWatch({
        control,
    });

    const checkedObjsCounter = useMemo(
        () =>
            checkedIds?.[data?.id] &&
            Object.entries(checkedIds[data.id]).reduce(
                (acc, [id, value]) => {
                    if (id !== 'parent') {
                        acc.totalCount += 1;
                        if (value) {
                            acc.checkedCount += 1;
                        }
                    }

                    return acc;
                },
                { checkedCount: 0, totalCount: 0 },
            ),
        [checkedIds, data?.id],
    );
    const isChildAndDisabled = useMemo(
        () =>
            getIsChildFieldDisabled({
                checkedIds: checkedIds?.[data?.parentId],
                data,
            }),
        [checkedIds, data],
    );

    const isParentAndDisabled = useMemo(() => {
        if (checkedIds[data?.id]?.parent) {
            if ('workOrderChangeObjects' in data) {
                return data?.workOrderChangeObjects?.some((obj) => obj?.isIncluded && !obj?.canRemoveOther);
            }

            return data?.workOrderObjects?.some((obj) => obj?.isIncluded && !obj?.canRemoveOther);
        }

        if (!checkedIds[data?.id]?.parent) {
            if ('workOrderChangeObjects' in data) {
                return data?.workOrderChangeObjects?.some((obj) => !obj?.isIncluded && !obj?.canAddOther);
            }

            return data?.workOrderObjects?.some((obj) => !obj?.isIncluded && !obj?.canAddOther);
        }

        return false;
    }, [checkedIds, data]);

    const childTooltip = useMemo(() => {
        if (isChildAndDisabled) {
            if (data?.hasActualCost && data?.isIncluded && (isWorkOrderChange || data?.isActKC2Cost)) {
                return 'Имеется фактическое выполнение в рамках объекта строительства';
            }

            if (!data?.canAddOther && !data?.isIncluded && !isWorkOrderChange) {
                return 'Для добавления записи необходимо создать Акт КС-2 в одном из статусов: «В работе» или «На проверке»';
            }
        }

        return '';
    }, [
        data?.canAddOther,
        data?.hasActualCost,
        data?.isActKC2Cost,
        data?.isIncluded,
        isChildAndDisabled,
        isWorkOrderChange,
    ]);

    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            {data.nestLevel === 0 ? (
                <ComponentWithTooltip
                    tooltipText={`${checkedObjsCounter?.checkedCount}/${checkedObjsCounter?.totalCount}
                                ${noun(checkedObjsCounter?.totalCount, ...['объекта', 'объектов'])}`}
                    styles={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <ControlledCheckbox
                        name={`checkedIds.${data?.id}.parent`}
                        control={control}
                        disabled={isParentAndDisabled}
                    />
                </ComponentWithTooltip>
            ) : (
                <ComponentWithTooltip
                    tooltipText={childTooltip}
                    styles={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <ControlledCheckbox
                        name={`checkedIds.${data?.parentId}.${data?.objectId}`}
                        control={control}
                        disabled={isChildAndDisabled}
                    />
                </ComponentWithTooltip>
            )}
        </div>
    );
};

CheckBoxRenderer.displayName = 'CheckBoxRenderer';
SeasonRenderer.displayName = 'SeasonRenderer';
NoteColumnRenderer.displayName = 'NoteColumn';
